import React, { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import WorkflowSelection from './WorkflowSelection';
import { WORKFLOWS } from './workflowConfigs';

interface LocationState {
  inputImages?: string[];
  generatedImages?: string[];
  workflowType?: string;
  modelImageUrl?: string;
  backgroundImageUrl?: string;
}

/**
 * AI Workflow container: shows workflow selection, then delegates to
 * the selected workflow component. This is now data-driven via WORKFLOWS config.
 */
const AIWorkflow: React.FC = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const state = location.state as LocationState | undefined;

  const queryType = searchParams.get('type');
  const [selectedWorkflowType, setSelectedWorkflowType] = useState<string | null>(queryType || state?.workflowType || null);

  useEffect(() => {
    const type = searchParams.get('type');
    if (type) {
      setSelectedWorkflowType(type);
    } else if (!state?.workflowType) {
      setSelectedWorkflowType(null);
    }
  }, [searchParams, state?.workflowType]);

  const handleWorkflowSelect = (workflowId: string) => {
    setSelectedWorkflowType(workflowId);
  };

  const handleBackToWorkflows = () => {
    setSelectedWorkflowType(null);
  };

  const selectedWorkflow = WORKFLOWS.find(w => w.id === selectedWorkflowType);

  if (selectedWorkflow) {
    const { Component } = selectedWorkflow;
    return (
      <Component
        onBackToWorkflows={handleBackToWorkflows}
        initialInputImages={state?.inputImages}
        initialGeneratedImages={state?.generatedImages}
        initialModelImageUrl={state?.modelImageUrl}
        initialBackgroundImageUrl={state?.backgroundImageUrl}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/30 p-6">
      <div className="w-full max-w-6xl">
        <h1 className="text-xl font-semibold text-gray-900 mb-5">AI Workflows</h1>
        <WorkflowSelection
          onSelectWorkflow={handleWorkflowSelect}
          selectedWorkflowId={selectedWorkflowType}
        />
      </div>
    </div>
  );
};

export default AIWorkflow;
